import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { useNavigate } from "@tanstack/react-router";
import { User, UserCircle } from "lucide-react";
import { useCallback } from "react";
import { PractitionerDisplay } from "@/shared/common/practitioner-display";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { patientDisplayName } from "@/shared/lib/patient-utils";
import { getPractitionerName } from "@/shared/lib/practitioner-utils";
import { useAppForm, useTaskFormData } from "../forms/use-task-form";
import { TASK_PRIORITY_OPTIONS, TASK_STATUS_OPTIONS } from "../lib/task-options";

export interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

export function CreateTaskDialog({ open, onClose, onExited }: CreateTaskDialogProps) {
  const navigate = useNavigate();

  const onSuccess = useCallback(() => {
    onClose();
    navigate({ to: "/tasks" });
  }, [navigate, onClose]);

  const {
    defaultValues,
    validators,
    handleSubmit,
    isPending,
    patients,
    practitioners,
    isLoadingPatients,
    isLoadingPractitioners,
  } = useTaskFormData({ onSuccess });

  const form = useAppForm({
    defaultValues,
    validators,
    onSubmit: ({ value }) => handleSubmit(value),
    onSubmitInvalid: focusFirstInvalid,
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) form.reset();
      if (!next) onClose();
    },
    [form, onClose],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} onOpenChangeComplete={(next) => !next && onExited()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogTitle className="sr-only">Create New Task</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-3"
        >
          {/* Title */}
          <form.AppField name="title">
            {(field) => <field.InputField variant="inline" placeholder="Task title" autoFocus />}
          </form.AppField>

          {/* Description editor */}
          <form.AppField name="description">
            {(field) => (
              <field.RichTextField
                placeholder="Add a task description..."
                className="text-sm prose prose-sm max-w-none [&_.tiptap]:min-h-[200px] [&_.tiptap]:outline-none"
              />
            )}
          </form.AppField>

          {/* Inline property bar */}
          <form.AppForm>
            <div className="flex items-center gap-1 flex-wrap">
              <form.AppField name="status">
                {(field) => <field.PropertyPickerListField options={TASK_STATUS_OPTIONS} />}
              </form.AppField>

              <form.AppField name="priority">
                {(field) => <field.PropertyPickerListField options={TASK_PRIORITY_OPTIONS} />}
              </form.AppField>

              <form.AppField name="patientId">
                {(field) => (
                  <field.PropertyPickerCommandField
                    options={patients.map((p) => {
                      const display = patientDisplayName(p) ?? "Unknown";
                      return {
                        value: p.patientId,
                        label: display,
                        icon: <User className="size-3.5" />,
                      };
                    })}
                    icon={<User className="size-3.5" />}
                    placeholder={isLoadingPatients ? "Loading..." : "Patient"}
                    disabled={isLoadingPatients}
                    renderItem={(option) => {
                      const patient = patients.find((p) => p.patientId === option.value);
                      if (!patient) return null;
                      const email = patient.telecom?.find((t) => t.system === "email")?.value;
                      const dob = patient.birthDate
                        ? new Date(patient.birthDate).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : null;
                      const details = [dob, email].filter(Boolean).join(" \u00b7 ");
                      return (
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          {details && <span className="text-xs text-muted-foreground">{details}</span>}
                        </div>
                      );
                    }}
                  />
                )}
              </form.AppField>

              <form.AppField name="practitionerId">
                {(field) => (
                  <field.PropertyPickerCommandField
                    options={practitioners
                      .filter((p) => p.practitionerId)
                      .map((p) => ({
                        value: p.practitionerId,
                        label: getPractitionerName(p),
                        displayLabel: <PractitionerDisplay practitionerId={p.practitionerId} />,
                      }))}
                    icon={<UserCircle className="size-3.5" />}
                    placeholder={isLoadingPractitioners ? "Loading..." : "Assignee"}
                    disabled={isLoadingPractitioners}
                  />
                )}
              </form.AppField>

              <form.AppField name="dueDate">{(field) => <field.PropertyPickerCalendarField />}</form.AppField>
            </div>
          </form.AppForm>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <form.Subscribe selector={(state) => state.values.title}>
              {(title) => (
                <Button type="submit" disabled={isPending || !title.trim()}>
                  {isPending ? "Creating..." : "Create Task"}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
